import { useControlledState } from "@react-stately/utils";
import React, {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";

import { SortDirection } from "@/components/list/SortDirectionToggle";
import { type ContentType, NonEmptyArray } from "@/types";
import { Modifier, selectInList } from "@/utils/selectInList";

import {
  DriveItem,
  DriveItemType,
  DriveSetSearchParams,
  DriveSortField,
  DriveTabType,
  DriveViewType,
  ItemTypeFilterType,
} from "./types";

interface DriveContextValue {
  // Actions state
  actionsDisabled: boolean;

  // Directory state
  currentDirectoryPath: string;
  setCurrentDirectoryPath: (path: string) => void;

  // Selection state
  selectableItemTypes: NonEmptyArray<DriveItemType>;
  selectableItemCount?: number;
  selectedItems: DriveItem[];
  setSelectedItems: (
    objectId: string | null,
    allObjects: DriveItem[],
    modifier?: Modifier | null,
  ) => void;
  selectAllItems: (allObjects: DriveItem[]) => void;
  onActivateItem: (item: DriveItem) => void;
  disabledItems: DriveItem[];

  // Search params
  searchParams: URLSearchParams;
  setSearchParams: DriveSetSearchParams;
  viewType: DriveViewType;
  driveTabType: DriveTabType;
  itemTypeFilter: ItemTypeFilterType;
  searchText: string;
  sortField: DriveSortField;
  sortDirection: SortDirection;
  contentType?: ContentType[];

  // Preview state
  previewIndex: number | null;
  setPreviewIndex: React.Dispatch<React.SetStateAction<number | null>>;
  isPreviewOpen: boolean;
  setIsPreviewOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

type DriveProviderProps = PropsWithChildren<{
  actionsDisabled?: boolean;
  currentDirectoryPath?: string;
  setCurrentDirectoryPath?: (path: string) => void;
  selectableItemTypes?: NonEmptyArray<DriveItemType>;
  selectableItemCount?: number;
  selectedItems?: DriveItem[];
  setSelectedItems?: (items: DriveItem[]) => void;
  onActivateItem?: (item: DriveItem) => void;
  disabledItems?: DriveItem[];
  searchParams?: URLSearchParams;
  setSearchParams?: DriveSetSearchParams;
  contentType?: ContentType[];
}>;

const DEFAULT_SORTING_DIRECTION_BY_FIELD: Record<
  DriveSortField,
  SortDirection
> = {
  rank: "desc",
  created_at: "desc",
  name: "asc",
};

const isDriveTabType = (value: unknown): value is DriveTabType => {
  return value === "explorer" || value === "all";
};

const getSortInfo = (
  searchParams: URLSearchParams,
  searchText: string,
): {
  sortField: DriveSortField;
  sortDirection: SortDirection;
} => {
  let sortField = (searchParams.get("sort") as DriveSortField) || "rank";
  if (sortField === "rank" && !searchText?.length) {
    sortField = "created_at";
  }
  const sortDirection =
    (sortField && (searchParams.get("dir") as SortDirection)) ||
    DEFAULT_SORTING_DIRECTION_BY_FIELD[sortField];
  return { sortField, sortDirection };
};

const extractSearchParams = (
  searchParams: URLSearchParams,
): {
  driveTabType: DriveTabType;
  itemTypeFilter: ItemTypeFilterType;
  viewType: DriveViewType;
  searchText: string;
  sortField: DriveSortField;
  sortDirection: SortDirection;
} => {
  let driveTabType = (searchParams.get("tab") as DriveTabType) || "explorer";
  if (!isDriveTabType(driveTabType)) {
    driveTabType = "explorer";
  }

  const itemTypeFilter =
    (searchParams.get("type") as ItemTypeFilterType) || "ALL";

  const viewType = (searchParams.get("viewType") as DriveViewType) || "grid";

  const searchText = searchParams.get("search") || "";

  const { sortField, sortDirection } = getSortInfo(searchParams, searchText);

  return {
    driveTabType,
    itemTypeFilter,
    viewType,
    searchText,
    sortField,
    sortDirection,
  };
};

const DriveContext = createContext<DriveContextValue | null>(null);

export const DriveProvider = ({
  children,
  actionsDisabled = false,
  currentDirectoryPath: initialDirectoryPath = "/",
  setCurrentDirectoryPath: initialSetCurrentDirectoryPath,
  selectableItemTypes,
  selectableItemCount,
  selectedItems: initialSelectedItems,
  setSelectedItems: initialSetSelectedItems,
  onActivateItem: onActivateItem,
  disabledItems = [],
  searchParams: providedSearchParams,
  setSearchParams: providedSetSearchParams,
  contentType,
}: DriveProviderProps) => {
  const [urlSearchParams] = useSearchParams();

  // Use provided searchParams if available, otherwise use URL search params
  const searchParamsToUse = providedSearchParams || urlSearchParams;
  const setSearchParamsToUse =
    providedSetSearchParams ||
    ((_) => {
      // This is a no-op when using URL search params directly
      console.error("setSearchParams called but no implementation provided");
    });

  // Extract search parameters from the provided searchParams
  const extractedParams = useMemo(
    () => extractSearchParams(searchParamsToUse),
    [searchParamsToUse],
  );

  const {
    driveTabType,
    itemTypeFilter,
    viewType,
    searchText,
    sortField,
    sortDirection,
  } = extractedParams;

  const [contextDirectoryPath, setContextDirectoryPath] = useControlledState(
    initialDirectoryPath,
    initialDirectoryPath,
    initialSetCurrentDirectoryPath,
  );

  const [selectedItems, setSelectedItems] = useControlledState<DriveItem[]>(
    initialSelectedItems,
    [],
    initialSetSelectedItems,
  );

  const setSelectedIds = useCallback(
    (
      objectId: string | null,
      allObjects: DriveItem[],
      modifier?: Modifier | null,
    ) => {
      if (!objectId) {
        setSelectedItems([]);
        return;
      }

      if (!allObjects) {
        throw new Error("No objects provided for selection");
      }

      const allIds = new Set<string>(
        // We need to filter the objects to only include those that are selectable
        allObjects
          .filter((obj) =>
            selectableItemTypes
              ? selectableItemTypes.includes(obj.__typename)
              : true,
          )
          .map((item) => item.id),
      );

      const selectedIds = new Set<string>(
        selectedItems?.map((item) => item.id),
      );

      const nextSelectedIds = selectInList<string>(
        objectId,
        allIds,
        selectedIds,
        modifier ?? undefined,
      );

      const nextSelectedItems = allObjects
        .filter((item) => nextSelectedIds.has(item.id))
        // Ensure that we select only up to the maximum selectable item count
        .slice(0, selectableItemCount);

      setSelectedItems(nextSelectedItems);

      const ix = Array.from(nextSelectedIds).indexOf(objectId);
      const defaultIx = nextSelectedItems.length === 0 ? null : 0;
      setPreviewIndex(ix === -1 ? defaultIx : ix);
    },
    [setSelectedItems],
  );

  const selectAllItems = useCallback(
    (allObjects: DriveItem[]) => {
      const selectedObjects = allObjects
        .filter((item) =>
          selectableItemTypes
            ? selectableItemTypes.includes(item.__typename)
            : true,
        )
        .slice(0, selectableItemCount);

      setSelectedItems(selectedObjects);
    },
    [selectableItemTypes, setSelectedItems],
  );

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  let currentDirectoryPath = contextDirectoryPath;
  if (driveTabType === "explorer") {
    currentDirectoryPath = currentDirectoryPath || "/";
  } else {
    currentDirectoryPath = "/";
  }

  const value: DriveContextValue = {
    actionsDisabled,
    currentDirectoryPath,
    setCurrentDirectoryPath: setContextDirectoryPath,
    selectableItemTypes: selectableItemTypes ?? [
      "File",
      "Asset",
      "Style",
      "ReferenceSet",
      "Directory",
    ],
    selectableItemCount,
    selectedItems,
    setSelectedItems: setSelectedIds,
    selectAllItems,
    onActivateItem: onActivateItem ?? (() => {}),
    disabledItems,
    searchParams: searchParamsToUse,
    setSearchParams: setSearchParamsToUse,
    viewType,
    itemTypeFilter,
    searchText,
    sortField,
    sortDirection,
    driveTabType,
    contentType,
    isPreviewOpen,
    setIsPreviewOpen: (next: boolean | ((prev: boolean) => boolean)) => {
      if (typeof next === "function") {
        next = next(isPreviewOpen);
      }

      setIsPreviewOpen(next);
      if (next && previewIndex === null) {
        setPreviewIndex(0);
      }
    },
    previewIndex,
    setPreviewIndex,
  };

  return (
    <DriveContext.Provider value={value}>{children}</DriveContext.Provider>
  );
};

export const useDrive = (): DriveContextValue => {
  const context = useContext(DriveContext);
  if (!context) {
    throw new Error("useDrive must be used within a DriveProvider");
  }
  return context;
};
